import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * AboutBeliefs v3 — three pillars, perfectly aligned.
 *
 *  - All 3 titles are one line each.
 *  - All 3 body paragraphs are intentionally written to wrap to the same
 *    number of lines (~3 lines each on desktop), eliminating the box-
 *    overlap / mis-alignment the user flagged.
 *  - Each card has matching min-heights for title + body so the vertical
 *    rhythm is identical across the row.
 */
const PILLARS = [
  {
    n: "01",
    eyebrow: "Why",
    title: "Connection is the multiplier.",
    body:
      "Speed, intelligence and accountability of the connection between consumer and brand is what separates winners from losers in financial services.",
  },
  {
    n: "02",
    eyebrow: "Approach",
    title: "Infrastructure, not features.",
    body:
      "Lead-gen platforms ship surface area. Connexis ships infrastructure — routing, qualification, compliance and settlement under one accountable API.",
  },
  {
    n: "03",
    eyebrow: "Standard",
    title: "Operators-first, always-on.",
    body:
      "Every feature ships because an operator asked for it. Sub-100ms routing, idempotent retries, on-call humans, FCA-aware compliance by default.",
  },
];

export default function AboutBeliefs() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="about-beliefs">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">
            What we believe
          </p>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-[56px] tracking-[-0.035em] font-semibold text-slate-900 leading-[1.02]">
            Building the network so financial brands don't have to.
          </h2>
          <p className="mt-5 text-[16.5px] text-slate-600 leading-relaxed max-w-2xl">
            Three beliefs hold the whole platform together. They've stayed unchanged since day one — they're how every routing decision, hire and roadmap call gets made.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-3 gap-x-10 gap-y-12">
          {PILLARS.map((p, i) => (
            <div
              key={i}
              className="relative pl-6 border-l border-slate-200 hover:border-[#0055FF]/40 transition-colors flex flex-col"
              data-testid={`belief-${i}`}
            >
              {/* Big number + eyebrow */}
              <div className="flex items-baseline gap-2.5">
                <span className="text-[44px] leading-none font-semibold tracking-[-0.04em] text-[#0055FF] tabular-nums">{p.n}</span>
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 font-medium">{p.eyebrow}</span>
              </div>

              {/* Title — forced single line on lg; min-height keeps the row aligned */}
              <h3 className="mt-5 text-[24px] sm:text-[26px] tracking-[-0.025em] font-semibold text-slate-900 leading-[1.18] lg:min-h-[36px]">
                {p.title}
              </h3>

              {/* Body — bodies are written to wrap to the same number of lines */}
              <p className="mt-4 text-[14.5px] text-slate-600 leading-[1.7] lg:min-h-[110px]">{p.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <Link
            to="/why-connexis"
            className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#0055FF] hover:underline"
          >
            The full story behind the platform <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
